import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, DataTable } from "@/components/common";
import { useApp, type Workspace } from "@/lib/store";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";

interface WorkspaceRecord extends Workspace { organization: string }
interface Usage { workspaces: { used: number; limit: number } }

export function Workspaces() {
  const qc = useQueryClient();
  const setWorkspaces = useApp((s) => s.setWorkspaces);
  const setWorkspace = useApp((s) => s.setWorkspace);
  const workspaces = useApp((s) => s.workspaces);
  const current = useApp((s) => s.workspaceId);
  const { canAdmin, isLoading: roleLoading } = useWorkspacePermissions();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [blocked, setBlocked] = useState<WorkspaceRecord | null>(null);

  const q = useQuery<{ results: WorkspaceRecord[] }>({
    queryKey: ["workspaces"],
    queryFn: () => rawFetch("/iam/workspaces/?page_size=100"),
    retry: false,
  });
  useEffect(() => {
    if (q.data?.results) setWorkspaces(q.data.results);
  }, [q.data, setWorkspaces]);

  const active = q.data?.results.find((w) => w.id === current) ?? q.data?.results[0];
  const usage = useQuery<Usage>({
    queryKey: ["workspace-usage", active?.organization],
    queryFn: () => rawFetch(`/iam/organizations/${active!.organization}/usage/`),
    enabled: Boolean(active?.organization && canAdmin),
  });
  const limit = usage.data?.workspaces.limit;
  const used = usage.data?.workspaces.used;
  const planBlocked = limit !== undefined && used !== undefined && limit > 0 && used >= limit;
  const canCreate = canAdmin && !planBlocked;

  const create = useMutation({
    mutationFn: () => rawFetch<WorkspaceRecord>("/iam/workspaces/", {
      method: "POST",
      body: JSON.stringify({ organization: active!.organization, name: name.trim(), slug: slug.trim() }),
    }),
    onSuccess: (created) => {
      setWorkspaces([...workspaces.filter((w) => w.id !== created.id), created]);
      setWorkspace(created.id);
      setName("");
      setSlug("");
      setCreateOpen(false);
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      qc.invalidateQueries({ queryKey: ["layout-workspaces"] });
      qc.invalidateQueries({ queryKey: ["workspace-usage"] });
    },
  });
  const join = useMutation({
    mutationFn: (workspace: WorkspaceRecord) => rawFetch(
      `/iam/workspaces/${workspace.id}/join/`, { method: "POST" }),
    onSuccess: () => {
      setBlocked(null);
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      qc.invalidateQueries({ queryKey: ["layout-workspaces"] });
    },
  });
  function attemptAccess(workspace: WorkspaceRecord) {
    if (workspace.is_member !== false) { setWorkspace(workspace.id); return; }
    setBlocked(workspace);
    // The detail API records denied nonmember access, without exposing settings.
    void rawFetch(`/iam/workspaces/${workspace.id}/`).catch(() => undefined);
  }

  return <>
    <PageHeader title="Workspaces" subtitle="Choose a workspace for your team's knowledge and applications."
      actions={<button className="btn-primary" type="button" disabled={!canCreate || !active}
        onClick={() => setCreateOpen((open) => !open)}>Create workspace</button>} />
    {!roleLoading && !canAdmin && <p className="mb-4 text-sm text-muted" role="status">
      Only a tenant owner or admin can create a workspace.</p>}
    {planBlocked && <p className="mb-4 text-sm text-warn" role="status">
      This plan allows {limit} workspace{limit === 1 ? "" : "s"}; {used} already exist.
    </p>}
    {blocked && <div className="card mb-4 border-warn/40 text-sm" role="alert">
      You are not a member of {blocked.name}. Its content stays unavailable until you join.
      {canAdmin && <button type="button" className="btn ml-3" disabled={join.isPending}
        onClick={() => join.mutate(blocked)}>Join as admin</button>}
      {join.isError && <ErrorBox error={join.error} />}
    </div>}
    {createOpen && canCreate && active && <form className="card mb-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto]"
      onSubmit={(event) => { event.preventDefault(); create.mutate(); }}>
      <label className="text-sm">Name
        <input className="input mt-1 w-full" value={name} maxLength={200} required
          onChange={(event) => setName(event.target.value)} placeholder="Claims team" />
      </label>
      <label className="text-sm">URL name
        <input className="input mt-1 w-full" value={slug} maxLength={80} required
          pattern="[a-z0-9]+(?:-[a-z0-9]+)*" onChange={(event) => setSlug(event.target.value.toLowerCase())}
          placeholder="claims-team" />
      </label>
      <button className="btn-primary self-end" type="submit" disabled={create.isPending}>Create</button>
      {create.isError && <div className="sm:col-span-3"><ErrorBox error={create.error} /></div>}
    </form>}
    {q.isError && <ErrorBox error={q.error} />}
    <DataTable rows={q.data?.results ?? []} cols={[
      { header: "Name", cell: (w) => <div><div>{w.name}</div><div className="text-xs text-muted">{w.slug}</div></div> },
      { header: "Plan", cell: (w) => <span className="capitalize">{w.plan ?? "—"}</span> },
      { header: "Membership", cell: (w) => <span className="capitalize">{w.membership_role ?? "Not joined"}</span> },
      { header: "", width: "1%", cell: (w) => <button
        className={current === w.id ? "btn-primary" : "btn"}
        onClick={() => attemptAccess(w)}>
        {current === w.id ? "Active" : w.is_member === false ? "View access" : "Select"}
      </button> },
    ]} empty="No workspaces are visible to you. Ask a tenant owner or admin to add you." />
  </>;
}
